'use client';

import { ComponentRef, forwardRef, useEffect, useMemo, useState } from "react";
import { Button, ButtonOwnProps, ButtonProps, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Check, ContentCopy, Email, Error } from "@mui/icons-material";

interface EmailButtonProps {
    email: string;
}

enum CopyState {
    Running = 'running',
    Success = 'success',
    Error = 'error',
}

const EmailButton = forwardRef<ComponentRef<typeof Button>, ButtonProps & ButtonOwnProps & EmailButtonProps>(
    ({ email, ...props }, ref) => {

        const [copyState, setCopyState] = useState<CopyState | null>(null);

        useEffect(() => {
            if (copyState === CopyState.Success || copyState === CopyState.Error) {
                setTimeout(() => setCopyState(null), 1500);
            }
        }, [copyState]);

        const copyEmailToClipboard = () => {
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                setCopyState(CopyState.Running);
                navigator.clipboard.writeText(email)
                    .then(() => {
                        setCopyState(CopyState.Success);
                    })
                    .catch((err: unknown) => {
                        if (err instanceof Error) {
                            // @ts-expect-error - ignore
                            console.error('Failed to copy email: ', err.message);
                        } else {
                            console.error('Failed to copy email: ', err);
                        }
                        setCopyState(CopyState.Error);
                    });
            }
        };

        const copyStateIcon = useMemo(() => {
            switch (copyState) {
                case CopyState.Running:
                    return <CircularProgress size={20} />;
                case CopyState.Success:
                    return <Check />;
                case CopyState.Error:
                    return <Error />;
                default:
                    return <ContentCopy />;
            }
        }, [copyState]);

        return (
            <Tooltip
                title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography>{email}</Typography>
                        {/* TODO: replace with {@link CopyButton}  */}
                        <Button
                            onClick={copyEmailToClipboard}
                            sx={{
                                paddingX: 0,
                                minWidth: '32px',
                            }}
                        >
                            {copyStateIcon}
                        </Button>
                    </Stack>
                }
                placement="top"
                arrow
            >
                <Button
                    ref={ref}
                    {...props}
                    variant="outlined"
                    startIcon={<Email />}
                    href={`mailto:${email}`}
                    sx={{
                        ...props.sx,
                        px: 1.5,
                        position: 'relative',
                        justifyContent: 'center',
                        pl: (theme) => `calc(${theme.spacing(4)} + 4px)`,
                        '& .MuiButton-startIcon': {
                            position: 'absolute',
                            left: (theme) => theme.spacing(2),
                            marginRight: 0,
                        },
                        textAlignLast: 'center'
                    }}
                    fullWidth
                >
                    <Typography
                        component="span"
                        noWrap
                        sx={{
                            color: 'inherit',
                            font: 'inherit',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            flexGrow: 1,
                        }}
                        title={email}
                    >
                        {email}
                    </Typography>
                </Button>
            </Tooltip>
        );
    }
);

EmailButton.displayName = 'EmailButton';
export default EmailButton;
